import React, { createContext, useContext, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import HighchartsCore from 'highcharts'

const ShowAllContext = createContext(false)

const isShowAllSearch = (search) => {
  if (!search || typeof search !== 'string') return false
  try {
    const params = new URLSearchParams(search)
    const show = params.get('show')
    return typeof show === 'string' && show.toLowerCase() === 'all'
  } catch (error) {
    return false
  }
}

const getShowAllLayoutElements = () => {
  if (typeof document === 'undefined') return []
  return Array.from(
    document.querySelectorAll('[data-popover-content], [data-show-all-menu]')
  )
}

const getDocumentGeometrySignature = (elements) => {
  const scrollX = window.scrollX || window.pageXOffset || 0
  const scrollY = window.scrollY || window.pageYOffset || 0
  return elements
    .map((element) => {
      const rect = element.getBoundingClientRect()
      return [
        Math.round(rect.left + scrollX),
        Math.round(rect.top + scrollY),
        Math.round(rect.width),
        Math.round(rect.height),
      ].join(':')
    })
    .join('|')
}

export const ShowAllProvider = ({ search, children }) => {
  // Gatsby's static HTML is generated without the browser query string. Apply
  // screenshot mode after hydration so ?show=all cannot change the first
  // client tree before React claims the server markup.
  const [hasMounted, setHasMounted] = React.useState(false)
  useEffect(() => {
    setHasMounted(true)
  }, [])

  const value = useMemo(
    () => hasMounted && isShowAllSearch(search),
    [hasMounted, search]
  )

  useEffect(() => {
    if (typeof document === 'undefined') return
    if (value) {
      document.body.setAttribute('data-show-all', 'true')
    } else {
      document.body.removeAttribute('data-show-all')
    }
  }, [value])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined
    }

    const body = document.body
    if (!value) {
      body.removeAttribute('data-show-all-ready')
      return undefined
    }

    let cancelled = false
    let frame = null
    let previousSignature = null
    let stableFrames = 0
    let resizeObserver = null
    let observedElements = []

    const markNotReady = () => {
      body.setAttribute('data-show-all-ready', 'false')
      previousSignature = null
      stableFrames = 0
    }

    const scheduleCheck = () => {
      if (cancelled) return
      markNotReady()
      if (frame === null) {
        frame = window.requestAnimationFrame(checkLayout)
      }
    }

    const observeLayoutElements = () => {
      const nextElements = getShowAllLayoutElements()
      const hasSameElements =
        observedElements.length === nextElements.length &&
        observedElements.every((element, index) => element === nextElements[index])
      if (hasSameElements && resizeObserver) return
      if (resizeObserver) resizeObserver.disconnect()
      observedElements = nextElements
      if (typeof ResizeObserver !== 'function') return
      resizeObserver = new ResizeObserver(scheduleCheck)
      nextElements.forEach((element) => {
        resizeObserver.observe(element)
      })
    }

    const checkLayout = () => {
      frame = null
      if (cancelled) return

      const popoverElements = Array.from(
        document.querySelectorAll('[data-popover-content]')
      )
      const allPopoversPositioned = popoverElements.every(
        (element) => element.dataset.showAllPositioned === 'true'
      )
      const fontsReady = !document.fonts || document.fonts.status === 'loaded'

      observeLayoutElements()

      if (!allPopoversPositioned || !fontsReady) {
        frame = window.requestAnimationFrame(checkLayout)
        return
      }

      const signature = getDocumentGeometrySignature(getShowAllLayoutElements())
      if (signature === previousSignature) {
        stableFrames += 1
      } else {
        previousSignature = signature
        stableFrames = 0
      }

      if (stableFrames >= 1) {
        body.setAttribute('data-show-all-ready', 'true')
        return
      }

      frame = window.requestAnimationFrame(checkLayout)
    }

    const mutationObserver =
      typeof MutationObserver === 'function'
        ? new MutationObserver(scheduleCheck)
        : null
    if (mutationObserver) {
      mutationObserver.observe(body, {
        attributes: true,
        attributeFilter: ['data-show-all-positioned'],
        childList: true,
        subtree: true,
      })
    }

    window.addEventListener('resize', scheduleCheck)
    markNotReady()
    observeLayoutElements()
    scheduleCheck()

    if (document.fonts?.ready) {
      document.fonts.ready.then(scheduleCheck).catch(() => {})
    }

    return () => {
      cancelled = true
      if (frame !== null) window.cancelAnimationFrame(frame)
      if (resizeObserver) resizeObserver.disconnect()
      if (mutationObserver) mutationObserver.disconnect()
      window.removeEventListener('resize', scheduleCheck)
      body.removeAttribute('data-show-all-ready')
    }
  }, [value])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const className = 'show-all-highcharts'
    if (value) {
      document.body.classList.add(className)
    } else {
      document.body.classList.remove(className)
    }
    return () => {
      document.body.classList.remove(className)
    }
  }, [value])

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined
    if (!value) return undefined

    const Highcharts =
      typeof window !== 'undefined'
        ? (window.Highcharts = window.Highcharts || HighchartsCore)
        : HighchartsCore
    if (!Highcharts || !Highcharts.Chart) return undefined

    const originalHideMenu = Highcharts.Chart.prototype.hideMenu
    const patchedCharts = new WeakSet()

    if (typeof originalHideMenu === 'function') {
      Highcharts.Chart.prototype.hideMenu = function hideMenuPatched() {}
    }

    const ensurePatched = (chart) => {
      if (!chart || patchedCharts.has(chart)) return
      if (typeof chart.hideMenu === 'function' && chart.hideMenu.name !== 'hideMenuPatched') {
        chart.hideMenu = function hideMenuPatched() {}
      }
      patchedCharts.add(chart)
    }

    const openMenuForChart = (chart) => {
      if (!chart) return
      ensurePatched(chart)

      const container =
        chart.renderTo ||
        (chart.container && chart.container.parentNode) ||
        chart.container ||
        null
      if (!(container instanceof Element)) return

      const menuVisible = () => {
        const menu =
          chart.exporting?.contextMenuEl ||
          container.querySelector('.highcharts-contextmenu') ||
          document.querySelector(
            `.highcharts-contextmenu[data-chart-index="${chart.index}"]`
          ) ||
          null
        if (!menu) return false
        const computedStyle =
          typeof window.getComputedStyle === 'function'
            ? window.getComputedStyle(menu)
            : null
        const display = menu.style?.display || computedStyle?.display || 'none'
        return display !== 'none'
      }
      if (menuVisible()) return

      const svgButton =
        container.querySelector('.highcharts-contextbutton') ||
        (Array.isArray(chart.exportSVGElements)
          ? chart.exportSVGElements
              .map((item) => item?.element || item)
              .find((el) => {
                if (!el || typeof el.getAttribute !== 'function') return false
                const cls = String(el.getAttribute('class') || '')
                return cls.includes('highcharts-contextbutton')
              })
          : null)

      if (!svgButton) return

      const triggerClick = (el) => {
        if (!el) return false
        try {
          const event = new window.MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          })
          el.dispatchEvent(event)
          return true
        } catch {
          if (typeof el.onclick === 'function') {
            try {
              el.onclick()
              return true
            } catch {
              return false
            }
          }
        }
        return false
      }

      let opened = false
      if (svgButton instanceof Element || typeof svgButton.dispatchEvent === 'function') {
        opened = triggerClick(svgButton)
      }

      if (
        !opened &&
        chart.exportButton &&
        chart.exportButton.element &&
        typeof chart.exportButton.element.dispatchEvent === 'function'
      ) {
        opened = triggerClick(chart.exportButton.element)
      }

      if (!opened && chart.exporting && typeof chart.exporting.contextMenu === 'function') {
        try {
          const button =
            chart?.exportButton ||
            (chart.exporting?.buttons && chart.exporting.buttons[0]) ||
            null
          const menuItems =
            chart.exporting?.options?.buttons?.contextButton?.menuItems ||
            chart.options?.exporting?.buttons?.contextButton?.menuItems ||
            []
          const x =
            (button && (button.x || button.translateX)) ||
            chart.plotLeft ||
            0
          const y =
            (button && (button.y || button.translateY)) ||
            chart.plotTop ||
            0
          const width = button?.width || 24
          const height = button?.height || 24
          chart.exporting.contextMenu(
            'contextmenu',
            menuItems,
            x,
            y,
            width,
            height,
            button
          )
          opened = true
        } catch {
          // ignore
        }
      }

      if (!opened) {
        const buttonCandidates = [
          chart.exportingGroup?.element,
          chart.exportButton?.element,
          chart.exportSVGElements && chart.exportSVGElements[0]?.element,
        ].filter(Boolean)
        buttonCandidates.forEach((candidate) => triggerClick(candidate))
      }

      if (opened && chart.exporting) {
        chart.exporting.openMenu = true
        const menu =
          chart.exporting.contextMenuEl ||
          container.querySelector('.highcharts-contextmenu')
        if (menu) {
          if (typeof menu.hideMenu === 'function') {
            menu.hideMenu = function noop() {}
          }
          if (chart.index !== undefined && chart.index !== null) {
            menu.setAttribute('data-chart-index', String(chart.index))
          }
          if (menu.style) {
            menu.style.display = 'block'
            menu.style.visibility = 'visible'
            menu.style.pointerEvents = 'auto'
          }
        }
      }
    }

    const openMenus = () => {
      const charts = Array.isArray(Highcharts.charts) ? Highcharts.charts : []
      charts.forEach((chart) => {
        if (!chart) return
        openMenuForChart(chart)
      })
    }

    const initial = window.setTimeout(openMenus, 0)
    const interval = window.setInterval(openMenus, 1000)

    const detach = []
    if (typeof Highcharts.addEvent === 'function' && Highcharts.Chart) {
      detach.push(
        Highcharts.addEvent(Highcharts.Chart, 'load', function handleLoad() {
          openMenuForChart(this)
        })
      )
      detach.push(
        Highcharts.addEvent(Highcharts.Chart, 'render', function handleRender() {
          openMenuForChart(this)
        })
      )
    }

    let observer = null
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(openMenus)
      observer.observe(document.body, { childList: true, subtree: true })
    }

    return () => {
      window.clearTimeout(initial)
      window.clearInterval(interval)
      if (observer) observer.disconnect()
      detach.forEach((remove) => {
        if (typeof remove === 'function') remove()
      })

      if (typeof originalHideMenu === 'function') {
        Highcharts.Chart.prototype.hideMenu = originalHideMenu
      }
    }
  }, [value])

  return (
    <ShowAllContext.Provider value={value}>
      {children}
    </ShowAllContext.Provider>
  )
}

ShowAllProvider.propTypes = {
  search: PropTypes.string,
  children: PropTypes.node.isRequired,
}

export const useShowAll = () => useContext(ShowAllContext)

export default ShowAllContext
